/**
 * ExecutionDetail Component
 *
 * Displays detailed information about a single execution.
 */
import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import {
  useExecution,
  useCancelExecution,
  useReplayExecution,
  getExecutionStatusColor,
  getExecutionStatusIcon,
  formatDuration,
  formatCents,
  formatTokens,
} from '../../hooks/useExecution';
import { Execution } from '../../types/execution';

interface ExecutionDetailProps {
  /** Execution ID to display */
  executionId: string;
  /** Whether to show back navigation */
  showBackLink?: boolean;
  /** Custom back link URL */
  backLinkUrl?: string;
  /** Custom back link text */
  backLinkText?: string;
}

export function ExecutionDetail({
  executionId,
  showBackLink = true,
  backLinkUrl = '/executions',
  backLinkText = 'Back to Executions',
}: ExecutionDetailProps) {
  const { data: execution, isLoading, error } = useExecution(executionId, {
    refetchInterval: 2000,
    refetchWhileRunning: true,
  });

  const cancelExecution = useCancelExecution();
  const replayExecution = useReplayExecution();
  const navigate = useNavigate();
  const location = useLocation();

  const handleCancel = async () => {
    if (!execution) return;
    await cancelExecution.mutateAsync(execution.id);
  };

  const handleReplay = async () => {
    if (!execution) return;
    const created = await replayExecution.mutateAsync(execution.id);
    // Navigate to the fresh run (same route, new id).
    navigate(location.pathname.replace(execution.id, created.id));
  };

  if (isLoading) {
    return <ExecutionDetailSkeleton />;
  }

  if (error || !execution) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="text-6xl mb-4">❌</div>
          <CardTitle className="mb-2">Execution Not Found</CardTitle>
          <CardDescription className="mb-4">
            The execution you're looking for doesn't exist or has been deleted.
          </CardDescription>
          <Button asChild>
            <Link to={backLinkUrl}>{backLinkText}</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Link */}
      {showBackLink && (
        <Link
          to={backLinkUrl}
          className="text-sm text-primary hover:underline inline-block"
        >
          ← {backLinkText}
        </Link>
      )}

      {/* Header Card */}
      <ExecutionHeader
        execution={execution}
        onCancel={handleCancel}
        isCancelling={cancelExecution.isPending}
        onReplay={handleReplay}
        isReplaying={replayExecution.isPending}
      />

      {/* Info Cards */}
      <ExecutionInfoCards execution={execution} />

      {/* Input Section */}
      <ExecutionInputSection execution={execution} />

      {/* Output/Error Section */}
      <ExecutionOutputSection execution={execution} />

      {/* Logs Section */}
      <ExecutionLogsSection execution={execution} />
    </div>
  );
}

/**
 * Execution Header Component
 */
/**
 * What a tool call returned, summarised by the backend and stored on the run.
 * Counts only — never the payload. A step that processed nothing looks identical
 * to a broken condition unless the run says how much was offered to it.
 */
type ToolResultSummary = {
  ok?: boolean;
  records?: number;
  status?: number;
  error?: string;
  /** Fields the agent asked for that were absent (or null), per field. */
  missing?: Record<string, number>;
};

function ExecutionHeader({
  execution,
  onCancel,
  isCancelling,
  onReplay,
  isReplaying,
}: {
  execution: Execution;
  onCancel: () => void;
  isCancelling: boolean;
  onReplay: () => void;
  isReplaying: boolean;
}) {
  const canCancel =
    execution.status === 'PENDING' || execution.status === 'RUNNING';
  // Replay makes sense once a run has finished (any terminal state).
  const canReplay = ['COMPLETED', 'FAILED', 'CANCELLED', 'TIMEOUT'].includes(
    execution.status
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <span className="text-5xl">
              {getExecutionStatusIcon(execution.status)}
            </span>
            <div>
              <CardTitle className="text-2xl mb-2 flex items-center gap-3">
                Execution Details
                <span
                  className={`px-3 py-1 text-sm rounded-full font-medium ${getExecutionStatusColor(
                    execution.status
                  )}`}
                >
                  {execution.status}
                </span>
              </CardTitle>
              <CardDescription className="text-base">
                Agent:{' '}
                <Link
                  to={`/agents/${execution.agentId}`}
                  className="text-primary hover:underline"
                >
                  {execution.agentName}
                </Link>
              </CardDescription>
              <p className="text-sm text-muted-foreground mt-1 font-mono">
                ID: {execution.id}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            {canCancel && (
              <Button
                variant="destructive"
                onClick={onCancel}
                disabled={isCancelling}
              >
                {isCancelling ? 'Cancelling...' : 'Cancel Execution'}
              </Button>
            )}
            {canReplay && (
              <Button onClick={onReplay} disabled={isReplaying}>
                {isReplaying ? 'Replaying…' : '🔁 Replay'}
              </Button>
            )}
            <Button variant="outline" asChild>
              <Link to={`/agents/${execution.agentId}`}>View Agent</Link>
            </Button>
          </div>
        </div>
      </CardHeader>
    </Card>
  );
}

/**
 * Execution Info Cards
 */
function ExecutionInfoCards({ execution }: { execution: Execution }) {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Priority
          </CardTitle>
        </CardHeader>
        <CardContent>
          <span
            className={`px-3 py-1 text-sm rounded-full font-medium ${getPriorityColor(
              execution.priority
            )}`}
          >
            {execution.priority}
          </span>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Duration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold">
            {formatDuration(execution.duration)}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Tokens
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold">
            {formatTokens(execution.tokensUsed)}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Cost
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold">
            {formatCents(execution.costCents)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">Estimated</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Started
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm font-medium">
            {formatDate(execution.startedAt)}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Completed
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm font-medium">
            {formatDate(execution.completedAt)}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Execution Input Section
 */
function ExecutionInputSection({ execution }: { execution: Execution }) {
  const hasInput = execution.input && Object.keys(execution.input).length > 0;

  // Hide section entirely when no input data
  if (!hasInput) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Input</CardTitle>
        <CardDescription>Data provided to the agent</CardDescription>
      </CardHeader>
      <CardContent>
        <pre className="bg-slate-100 p-4 rounded-lg overflow-x-auto text-sm max-h-64">
          {JSON.stringify(execution.input, null, 2)}
        </pre>
      </CardContent>
    </Card>
  );
}

/**
 * Execution Output/Error Section
 */
function ExecutionOutputSection({ execution }: { execution: Execution }) {
  // Declared before the early return below: hooks must run in the same order on
  // every render, and this component returns null for a run that has not
  // finished.
  const [copied, setCopied] = useState(false);
  const isCompleted = execution.status === 'COMPLETED';
  const isFailed =
    execution.status === 'FAILED' || execution.status === 'TIMEOUT';
  const out = (execution.output ?? {}) as Record<string, unknown>;
  const hasOutput = Object.keys(out).length > 0;

  if (!isCompleted && !isFailed) {
    return null;
  }

  // The agent result carries the text answer, any tools it called, and (when the
  // agent is set to structured output) a parsed `data` object.
  const answer = typeof out.output === 'string' ? out.output : null;
  const toolCalls = Array.isArray(out.tool_invocations)
    ? (out.tool_invocations as { tool?: string; args?: unknown; result?: ToolResultSummary }[])
    : [];
  const data =
    out.data && typeof out.data === 'object' && !Array.isArray(out.data)
      ? (out.data as Record<string, unknown>)
      : null;

  const copyOutput = async () => {
    const text = isFailed && execution.error
      ? execution.error
      : JSON.stringify(execution.output ?? {}, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be denied (permissions, insecure origin). Say so rather
      // than leaving the button looking like it worked.
      setCopied(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle>{isFailed ? 'Error' : 'Output'}</CardTitle>
            <CardDescription>
              {isFailed
                ? 'Error information from the execution'
                : 'What the agent returned — including any tools it used'}
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={copyOutput}>
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isFailed && execution.error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700 font-mono text-sm whitespace-pre-wrap">
              {execution.error}
            </p>
          </div>
        ) : !hasOutput ? (
          <p className="text-muted-foreground text-sm">
            No output data was returned.
          </p>
        ) : (
          <>
            {answer && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Answer</p>
                <div className="bg-slate-100 p-4 rounded-lg text-sm whitespace-pre-wrap max-h-96 overflow-y-auto">
                  {answer}
                </div>
              </div>
            )}

            {toolCalls.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  Tools used ({toolCalls.length})
                </p>
                <ul className="space-y-1">
                  {toolCalls.map((t, i) => (
                    <li
                      key={i}
                      className="text-sm border rounded-md px-3 py-2 bg-white"
                    >
                      <span className="font-mono font-medium">{t.tool ?? 'tool'}</span>
                {/* "returned", not just "records": the word names WHICH SIDE the zero
                    came from. AleksGorbatov, 9 Sept 2026 — "leaving the destination
                    side unbuilt is fine as long as the label says which side the zero
                    came from". A bare "0 records" could mean the source handed over
                    nothing or the destination accepted nothing; this claims only the
                    first, which is the only one we can prove. */}
                {typeof t.result?.status === 'number' && t.result.status >= 400 ? (
                  /* Status first: since 8 Oct 2026 a refused call is stored as ok: false,
                     and "HTTP 403" says more than a bare "failed". Older runs still carry
                     ok: true beside the 403 and render the same way. */
                  <span className="ml-1 font-medium text-amber-700">
                    HTTP {t.result.status}
                    {typeof t.result.records === 'number'
                      ? `, ${t.result.records} records returned`
                      : ''}
                  </span>
                ) : t.result?.ok === false ? (
                  <span className="ml-1 text-red-600" title={t.result.error}>
                    failed
                  </span>
                ) : typeof t.result?.records === 'number' ? (
                  <span className="ml-1 font-medium text-slate-700">
                    {t.result.records} records returned
                  </span>
                ) : null}
                {/* A 200 whose upstream renamed a field still reads as "10 records".
                    The field the agent asked for and did not get is the signal.
                    Reddit question, 8 Oct 2026. */}
                {t.result?.missing &&
                  Object.keys(t.result.missing).length > 0 && (
                    <span className="ml-1 font-medium text-amber-700">
                      ·{' '}
                      {Object.entries(t.result.missing)
                        .map(([field, n]) =>
                          typeof t.result?.records === 'number'
                            ? `${field} missing in ${n} of ${t.result.records}`
                            : `${field} missing in ${n}`
                        )
                        .join(', ')}
                    </span>
                  )}
                      {t.args != null && (
                        <span className="text-xs text-muted-foreground break-all">
                          {' '}
                          {JSON.stringify(t.args)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  Structured output
                </p>
                <pre className="bg-slate-100 p-4 rounded-lg overflow-x-auto text-sm max-h-64">
                  {JSON.stringify(data, null, 2)}
                </pre>
              </div>
            )}

            <details>
              <summary className="text-xs text-muted-foreground cursor-pointer">
                Raw output
              </summary>
              <pre className="mt-2 bg-slate-100 p-4 rounded-lg overflow-x-auto text-sm max-h-96">
                {JSON.stringify(execution.output, null, 2)}
              </pre>
            </details>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Execution Logs Section (placeholder for future implementation)
 */
function ExecutionLogsSection({ execution }: { execution: Execution }) {
  // This is a placeholder for execution logs
  // In a real implementation, logs would be fetched from a separate endpoint

  return (
    <Card>
      <CardHeader>
        <CardTitle>Execution Logs</CardTitle>
        <CardDescription>
          Detailed logs from the execution process
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm text-slate-100 max-h-96 overflow-y-auto">
          <LogEntry
            timestamp={execution.createdAt}
            level="INFO"
            message={`Execution ${execution.id} created`}
          />
          {execution.startedAt && (
            <LogEntry
              timestamp={execution.startedAt}
              level="INFO"
              message={`Execution started with priority ${execution.priority}`}
            />
          )}
          {execution.status === 'RUNNING' && (
            <LogEntry
              timestamp={new Date().toISOString()}
              level="INFO"
              message="Execution in progress..."
              isLive
            />
          )}
          {execution.completedAt && (
            <LogEntry
              timestamp={execution.completedAt}
              level={execution.status === 'COMPLETED' ? 'INFO' : 'ERROR'}
              message={
                execution.status === 'COMPLETED'
                  ? `Execution completed successfully in ${formatDuration(
                      execution.duration
                    )}`
                  : `Execution ${execution.status.toLowerCase()}: ${
                      execution.error || 'Unknown error'
                    }`
              }
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Log Entry Component
 */
function LogEntry({
  timestamp,
  level,
  message,
  isLive = false,
}: {
  timestamp: string;
  level: 'INFO' | 'WARN' | 'ERROR';
  message: string;
  isLive?: boolean;
}) {
  const levelColors = {
    INFO: 'text-blue-400',
    WARN: 'text-yellow-400',
    ERROR: 'text-red-400',
  };

  const time = new Date(timestamp).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return (
    <div className="flex gap-3 py-1">
      <span className="text-slate-500">{time}</span>
      <span className={`w-12 ${levelColors[level]}`}>[{level}]</span>
      <span className={isLive ? 'animate-pulse' : ''}>{message}</span>
    </div>
  );
}

/**
 * Loading Skeleton
 */
function ExecutionDetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-6 w-32 bg-slate-200 rounded animate-pulse" />
      <div className="h-40 bg-slate-100 rounded-lg animate-pulse" />
      <div className="grid gap-4 md:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 bg-slate-100 rounded-lg animate-pulse" />
        ))}
      </div>
      <div className="h-48 bg-slate-100 rounded-lg animate-pulse" />
    </div>
  );
}

/**
 * Helper to get priority badge color
 */
function getPriorityColor(priority: string): string {
  switch (priority) {
    case 'LOW':
      return 'bg-slate-100 text-slate-700';
    case 'NORMAL':
      return 'bg-blue-100 text-blue-700';
    case 'HIGH':
      return 'bg-orange-100 text-orange-700';
    case 'CRITICAL':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

export default ExecutionDetail;
